import { WebContainer, FileSystemTree, DirectoryNode } from '@webcontainer/api';
import { Artifact, Project } from '../types';
import { scaffoldFiles, ScaffoldFile } from './demoScaffold';

/**
 * Build pipeline for the in-browser jira-mock demo.
 *
 * The flow:
 *   1. Collect inputs — project artifacts (LLM-emitted) + vendored jira-mock
 *      files + static scaffold (package.json, tsconfig, webpack config, the
 *      DemoEntry shim).
 *   2. Assemble a WebContainer FileSystemTree from those three sources.
 *      Precedence: artifacts > scaffold > vendor. So an LLM that wants to
 *      override webpack.demo.config.js or the entry can.
 *   3. Boot WC (singleton), mount, npm install, npm run build:demo, read
 *      build-demo/ from the WC filesystem.
 *   4. Return the built file map (path -> contents) so the caller can POST
 *      it up to the orchestrator.
 *
 * Cross-cutting:
 *   - WebContainer.boot() can only run once per page load; we stash the
 *     instance at module scope.
 *   - Output streaming is exposed via the `onLog` callback so the UI can
 *     show progress.
 */

let bootedInstance: WebContainer | null = null;
let bootInFlight: Promise<WebContainer> | null = null;

export async function bootWebContainer(): Promise<WebContainer> {
  if (bootedInstance) return bootedInstance;
  if (bootInFlight) return bootInFlight;
  bootInFlight = WebContainer.boot({ coep: 'credentialless' }).then((wc) => {
    bootedInstance = wc;
    return wc;
  });
  return bootInFlight;
}

interface VendorFilesPayload {
  version: string;
  files: { path: string; contents: string }[];
}

let vendorCache: VendorFilesPayload | null = null;

export async function fetchVendoredJiraMock(): Promise<VendorFilesPayload> {
  if (vendorCache) return vendorCache;
  const res = await fetch('/jira-mock/files.json');
  if (!res.ok) throw new Error(`fetch jira-mock files.json: ${res.status}`);
  vendorCache = (await res.json()) as VendorFilesPayload;
  return vendorCache;
}

/* ------------------------------------------------------------------ */
/*  File tree assembly                                                */
/* ------------------------------------------------------------------ */

/**
 * Walks a slash-separated path and inserts a file leaf into a FileSystemTree,
 * creating directory nodes along the way. Mutates `root`.
 */
function insertFile(root: FileSystemTree, path: string, contents: string): void {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return;
  let cursor: FileSystemTree = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const seg = parts[i];
    const existing = cursor[seg];
    if (!existing) {
      const node: DirectoryNode = { directory: {} };
      cursor[seg] = node;
      cursor = node.directory;
    } else if ('directory' in existing) {
      cursor = existing.directory;
    } else {
      // A file already exists at this path segment — overwrite as a
      // directory; later code that wants to recover that file can re-emit it.
      const node: DirectoryNode = { directory: {} };
      cursor[seg] = node;
      cursor = node.directory;
    }
  }
  const leaf = parts[parts.length - 1];
  cursor[leaf] = { file: { contents } };
}

/**
 * Project artifacts are an array of `{ name, content }` pairs where `name`
 * is already a slash-separated relative path (e.g. `src/components/App.tsx`).
 * Steps with the relevant skillRefs are collected; we ignore everything else.
 *
 * The LLM doesn't have a uniform contract for which step emits build sources
 * yet — for now we accept artifacts from `generate`, `jira-mock-setup`,
 * `forge-craft`, and `build-forge-app`. Order matters for last-write-wins
 * within artifacts; earlier in this list = lower priority.
 */
const BUILD_SOURCE_STEPS = ['generate', 'jira-mock-setup', 'forge-craft', 'build-forge-app'];

export interface BuildInputs {
  project: Project;
  vendor: VendorFilesPayload;
  scaffold: ScaffoldFile[];
}

export function collectProjectArtifacts(project: Project): Artifact[] {
  const out: Artifact[] = [];
  for (const phase of project.phases) {
    for (const step of phase.steps) {
      const skill = step.skillRef ?? step.key;
      if (!BUILD_SOURCE_STEPS.includes(skill)) continue;
      for (const a of step.artifacts) out.push(a);
    }
  }
  return out;
}

export function assembleFileTree(inputs: BuildInputs): FileSystemTree {
  const tree: FileSystemTree = {};

  // 1) Vendor (lowest priority) — written under packages/jira-mock/.
  for (const f of inputs.vendor.files) {
    insertFile(tree, `packages/jira-mock/${f.path}`, f.contents);
  }

  // 2) Scaffold — package.json, tsconfig, webpack config, demo entry.
  for (const f of inputs.scaffold) {
    insertFile(tree, f.path, f.contents);
  }

  // 3) Project artifacts (highest priority) — last write wins, so the LLM
  // can override any scaffold or vendor file by emitting one with the same
  // path.
  const artifacts = collectProjectArtifacts(inputs.project);
  for (const a of artifacts) {
    // Binary artifacts (base64) aren't source files for the HTML demo build;
    // inserting their base64 as text would corrupt the tree, so skip them.
    if (a.encoding === 'base64') continue;
    insertFile(tree, a.name, a.content);
  }

  return tree;
}

/* ------------------------------------------------------------------ */
/*  Build runner                                                      */
/* ------------------------------------------------------------------ */

export type BuildPhase =
  | 'idle'
  | 'booting'
  | 'mounting'
  | 'installing'
  | 'building'
  | 'reading'
  | 'ready'
  | 'failed';

export interface BuildResult {
  phase: 'ready';
  files: Record<string, string>;
}

export interface BuildOptions {
  /** Streamed log output (stdout + stderr). Called with raw chunks. */
  onLog?: (chunk: string) => void;
  /** Phase transitions, for UI status pills. */
  onPhase?: (phase: BuildPhase) => void;
  /**
   * Whether to run `npm run start` (dev server, server-ready fires) or
   * `npm run build` (one-shot production build, no server). For the demo
   * persistence flow we want `build` — we read the static files from
   * build-demo/ and upload, not a live server URL.
   */
  command?: 'start' | 'build';
}

/**
 * Recursively read every file under `dir` in the WebContainer FS and return a
 * { relative-path -> contents } map. Used to collect build-demo/ for upload.
 */
async function readDirRecursive(
  wc: WebContainer,
  dir: string,
): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const entries = await wc.fs.readdir(dir, { withFileTypes: true });
  for (const entry of entries) {
    const childPath = `${dir}/${entry.name}`;
    if (entry.isDirectory()) {
      const nested = await readDirRecursive(wc, childPath);
      for (const [k, v] of Object.entries(nested)) out[k] = v;
    } else if (entry.isFile()) {
      const contents = await wc.fs.readFile(childPath, 'utf-8');
      out[childPath] = contents;
    }
  }
  return out;
}

export async function runDemoBuild(
  inputs: BuildInputs,
  opts: BuildOptions = {},
): Promise<BuildResult> {
  const log = (s: string) => opts.onLog?.(s);
  const phase = (p: BuildPhase) => opts.onPhase?.(p);
  const command = opts.command ?? 'build';

  phase('booting');
  log('[demo] booting WebContainer …\n');
  const wc = await bootWebContainer();

  phase('mounting');
  log('[demo] assembling project file tree …\n');
  const tree = assembleFileTree(inputs);
  await wc.mount(tree);
  log('[demo] mounted.\n');

  phase('installing');
  log('[demo] running `npm install` …\n');
  const install = await wc.spawn('npm', ['install']);
  install.output.pipeTo(new WritableStream({ write: (s) => log(s) }));
  const installExit = await install.exit;
  if (installExit !== 0) {
    phase('failed');
    throw new Error(`npm install exited with code ${installExit}`);
  }

  phase('building');
  log(`[demo] running \`npm run ${command}\` …\n`);
  const proc = await wc.spawn('npm', ['run', command]);
  proc.output.pipeTo(new WritableStream({ write: (s) => log(s) }));
  const buildExit = await proc.exit;
  if (buildExit !== 0) {
    phase('failed');
    throw new Error(`npm run ${command} exited with code ${buildExit}`);
  }

  phase('reading');
  log('[demo] reading build-demo/ …\n');
  const rawFiles = await readDirRecursive(wc, '/home/build-demo').catch(() =>
    readDirRecursive(wc, 'build-demo'),
  );
  // Strip whatever absolute prefix WebContainer used, keep paths relative to
  // build-demo/ — e.g. `build-demo/Demo/app.js` -> `Demo/app.js`.
  const files: Record<string, string> = {};
  for (const [k, v] of Object.entries(rawFiles)) {
    const idx = k.indexOf('build-demo/');
    const rel = idx === -1 ? k.replace(/^\/+/, '') : k.slice(idx + 'build-demo/'.length);
    files[rel] = v;
  }
  log(`[demo] captured ${Object.keys(files).length} files.\n`);

  phase('ready');
  return { phase: 'ready', files };
}
